import { ElementAttribute } from "../../../library-js/dom-tools/ElementAttribute";
import { ElementType } from "../../../library-js/dom-tools/ElementType";
import { SVGEditor } from "../../../library-js/svg/SVGEditor";
import { Inkscape } from "../../../library-js/svg/Inkscape";

import { PlayingSongInfo } from "./data/PlayingSongInfo";
import { RadioChannels, RadioChannelType } from "./data/RadioChannel";
import { RegExpUtility } from "../../../library-js/text/RegExpUtility";
import { MathX } from "../../../library-js/math/MathX";
import { DOMHitTest } from "../../../library-js/dom-tools/DOMHitTest";
import { Vector2 } from "three";
import { LegacyOnDragEvent } from "../../../library-js/dragging/LegacyOnDragEvent";
import { Rokojori } from "../Rokojori";
import { Radio } from "./Radio";
import { OnClick, OnDrag, OnMouseDown, OnMouseLeave, OnMouseMove, OnMouseUp, OnResize, OnTouchEnd, OnTouchMove, OnTouchStart } from "../../../library-js/dom-tools/EventListeners";
import { ClassFlag } from "../../../library-js/dom-tools/ClassFlag";
import { HTMLEditor } from "../../../library-js/dom-tools/HTMLEditor";
import { DOMOrientation } from "../../../library-js/dom/DOMOrientation";
import { AttributeValue } from "../../../library-js/dom-tools/AttributeValue";
import { SongInfo } from "./data/SongInfo";

export class RadioUI
{
  static starter = new ElementAttribute( "radio-starter" );
  static elementType      = new ElementType( "radio-ui" );
  static channelAttribute = new ElementAttribute( "channel" );

  static artists = new ElementAttribute( "artists" );
  static title  = new ElementAttribute( "title" );

  static nextArtists = new ElementAttribute( "next-artists" );
  static nextTitle  = new ElementAttribute( "next-title" );

  static duration  = new ElementAttribute( "duration" );
  static time  = new ElementAttribute( "time" );

  static y = new ElementAttribute( "y" );
  static x0 = new ElementAttribute( "x0" );
  static x1 = new ElementAttribute( "x1" );

  static x = new ElementAttribute( "x" );
  static y0 = new ElementAttribute( "y0" );
  static y1 = new ElementAttribute( "y1" );

  static a0 = new ElementAttribute( "a0" );
  static a1 = new ElementAttribute( "a1" );

  static origin = new ElementAttribute( "origin" );

  static playHead  = new ElementAttribute( "play-head" );
  static volumeKnob  = new ElementAttribute( "volume-knob" );
  static volumeInfo  = new ElementAttribute( "volume-info" );
  static volumeRange = new ElementAttribute( "volume-range" );

  static volumeSlider = new ElementAttribute( "volume-slider" );
  static start = new ElementAttribute( "start" );
  static end   = new ElementAttribute( "end" );

  static svgRotate = new ElementAttribute( "svg-rotate" )

  static fadeKnob = new ElementAttribute( "fade-knob" );
  static dragging = new ClassFlag( "dragging" );

  static wheel = new ElementType( "radio-ui-wheel" );
  static wheelPosition = new ElementAttribute( "wheel-position" );

  static landscapeLeft = new AttributeValue( RadioUI.wheelPosition, "landscape-left" ); 
  static landscapeRight = new AttributeValue( RadioUI.wheelPosition, "landscape-right" ); 

  static portraitTop = new AttributeValue( RadioUI.wheelPosition, "portrait-top" ); 
  static portraitBottom = new AttributeValue( RadioUI.wheelPosition, "portrait-bottom" ); 

  static radioButton = new ElementAttribute( "radio-button" );

  static sliderArea  = new ElementAttribute( "slider-area" );
  static radioSlider = new ElementAttribute( "radio-slider" ); 
  
  static faderArea  = new ElementAttribute( "fader-area" );
  static radioFader = new ElementAttribute( "radio-fader" ); 

  static playBar = new ElementAttribute( "play-bar" );


  static resizeListener:()=>void = null; 
  static readonly play  = "play";
  static readonly pause = "pause";
  static readonly next  = "next";

  static readonly bass    = "bass";
  static readonly treble  = "treble";

  // calc( ( 100vw / 1920 * 1080 - 1080px ) / 2 )
  _element:Element;
  _channel:RadioChannelType;
  
  get element()
  {
    return this._element;
  }

  get channel()
  {
    return this._channel;
  }

  constructor( element:Element )
  {
    this._element = element;
    this._channel = RadioUI.channelAttribute.from( element ) as RadioChannelType;

    this._addEventListener();   
  }

  
  static formatSeconds( duration:number )
  {
    duration = Math.round( duration );
    let minutes = RegExpUtility.prependZeros( Math.floor( duration / 60 ) + "", 2 );
    let seconds = RegExpUtility.prependZeros( ( duration % 60 ) + "", 2 );

    return `${minutes}:${seconds}`
  }

  _radioButtons:Element[] = null; 
  _sliderAreas:Element[]  = null;
  _faderAreas:Element[]   = null;

  onPlayPauseChange()
  {
    let isPlaying = Radio.$.player.isPlaying;

    this._radioButtons = this._radioButtons || RadioUI.radioButton.queryDocAll();

    this._radioButtons.forEach(
      ( rb )=>
      {   
        let type = RadioUI.radioButton.from( rb );
        let r = rb as HTMLElement;

        switch ( type )
        {
          case RadioUI.play:
          {
            r.style.display = ! isPlaying ? "inline" : "none";
            r.style.pointerEvents = ! isPlaying ? "all" : "none";
          }
          break;

          case RadioUI.pause:
          {
            r.style.display = isPlaying ? "inline" : "none";
            r.style.pointerEvents = isPlaying ? "all" : "none";
          }
          break;
        }
      }
    );

    Radio.wheel.forAllInDoc(
      ( w )=>
      {
        //SVGRotate.applyOnElement( r );
        let classFlag = new ClassFlag( "rotate-10s" );
        let ww = w as HTMLElement;

        if ( ! classFlag.in( w ) )
        {
          classFlag.set( w );
        }

        if ( ! isPlaying )
        {
          ww.style.animationPlayState = "paused";
        }
        else
        {
          ww.style.animationPlayState = "running";
        }

        
      }
    );
  }

  static _wheelPositions:Element[] = null;
  static _wheels:Element[] = null;

  static simpleModeFlag = new ClassFlag( "simple-mode" );

  static setSimpleMode( element:Element )
  {
    RadioUI.simpleModeFlag.set( element );  
  }

  static applyOnDocument()
  {
    this._wheelPositions = null;
    this._wheels = null;
    
    RadioUI.onResize();

    if ( RadioUI.resizeListener )
    {
      OnResize.remove( window, RadioUI.resizeListener );
    }

    RadioUI.resizeListener = ()=>
    {
      RadioUI.onResize();
    }

    OnResize.add( window, RadioUI.resizeListener );    

  }

  private static onResize()
  {    
    this._wheelPositions = this._wheelPositions || RadioUI.wheelPosition.queryDocAll();
    this._wheels = this._wheels || RadioUI.wheel.queryAll( document.body );

    this._wheelPositions.forEach( w => this.updatePosition( w ) );
  }

  private static updatePosition( wheelPosition:Element )
  {
    
    let type = RadioUI.wheelPosition.from( wheelPosition );
    let flag = new ClassFlag( type );
    let wheels = this._wheels.filter( w => flag.in( w ) );

    let centerY = DOMHitTest.getPageBox( wheelPosition ).getCenter( new Vector2() ).y;

    let offset = "";

    if ( RadioUI.landscapeLeft.in( wheelPosition )  || RadioUI.landscapeRight.in( wheelPosition ) )
    {
      offset = "5vw";
    }
    else if ( RadioUI.portraitTop.in( wheelPosition )  || RadioUI.portraitBottom.in( wheelPosition ) )
    {
      offset = "25vw";
    }

    wheels.forEach(
      ( w )=>
      {
        let wh = w as HTMLElement;
        wh.style.top = `calc( ${centerY}px - ${offset} )`;
      } 
    )


  }

  private _addEventListener()
  {
   

    let playBars = RadioUI.playBar.queryDocAll();
    console.log( "BARRS", playBars );
    playBars.forEach(
      ( pb ) =>
      {
        let updatePlayPosition = ( e:MouseEvent|TouchEvent )=>
        {
          

          let position = DOMHitTest.getNormalizedPointerPosition( e, pb );
          let playPosition = MathX.clamp01( position.x ) * Radio.$.player.songLength;
          let rounded = playPosition;
          console.log( "change position", e, position, playPosition );  

          console.log( Radio.$.player._audioPlayer._audioElement.currentTime );
          	
          ( ( Radio.$.player._audioPlayer._audioElement ) as any ).currentTime = rounded;

          console.log( Radio.$.player._audioPlayer._audioElement.currentTime );

          //Radio.$.player._audioPlayer.setPosition( playPosition );        

          //console.log( Radio.$.player._audioPlayer._audioElement.currentTime );
          ///Radio.$.player._audioPlayer._audioElement.currentTime = playPosition;

          //console.log( Radio.$.player._audioPlayer._audioElement.currentTime );
        }

        OnDrag.add( pb as HTMLElement, updatePlayPosition, { passive:true } );
      }
    )

    let volumeKnobs = RadioUI.volumeKnob.queryDocAll();

    volumeKnobs.forEach( ( volumeKnob ) => this._addVolumeChanger( volumeKnob ) );

    console.log( "ALL VS", RadioUI.volumeSlider.queryValueAllInDoc );

    RadioUI.volumeSlider.forEachInDoc ( vs => this._addTouchVolumeChanger( vs ) );


    let fadeKnobs = RadioUI.fadeKnob.queryDocAll();
    fadeKnobs.forEach( ( fadeKnob ) => this._removeOpacityAttribute( fadeKnob ) );

    this._sliderAreas = this._sliderAreas || RadioUI.sliderArea.queryDocAll();

    this.syncRadioSliders();

    console.log("SLIDER", this._sliderAreas );
    this._sliderAreas.forEach(
      ( sa )=>
      {
        this._addSliderAreaChanger( sa );
      }
    )

    this._faderAreas = this._faderAreas || RadioUI.faderArea.queryDocAll();

    this.syncRadioFaders();

    console.log("FADER", this._faderAreas );
    this._faderAreas.forEach(
      ( sa )=>
      {
        this._addFaderAreaChanger( sa );
      }
    )

    this._radioButtons = this._radioButtons || RadioUI.radioButton.queryDocAll();

    this._radioButtons.forEach(
      ( rb )=>
      {   
        let type = RadioUI.radioButton.from( rb );

        switch ( type )
        {
          case RadioUI.play:
          {
            OnClick.add( rb as HTMLElement, ()=> { console.log( "play" ); Radio.$.player._audioPlayer.play(); } );
          }
          break;

          case RadioUI.pause:
          {
            OnClick.add( rb as HTMLElement, ()=> { console.log( "pause" ); Radio.$.player._audioPlayer.pause(); } );
            Radio.$.player._audioPlayer.play();
          }
          break;

          case RadioUI.next:
          {
            OnClick.add( rb as HTMLElement, 
              ()=> 
              { 
                console.log( "pause" );
                Radio.$.player._audioPlayer.pause();
                
                setTimeout(
                  ()=>{ Radio.$.player.scheduleNext(); },
                  200
                )
                
              } 
            );
          }
          break;
        }
      }
    );
  }

  private _removeOpacityAttribute( fadeKnob:Element )
  {
    SVGEditor.opacity.removeFrom( fadeKnob );
  }

  private syncRadioSliders()
  {
    let radioSliders = RadioUI.radioSlider.queryDocAll();

    console.log( "SYNC RS", radioSliders );

    radioSliders.forEach(
      ( rs )=>
      {
        let value = 0;
        let target = RadioUI.radioSlider.from( rs );

        if ( RadioUI.bass === target )
        {
          value = Radio.$.player._audioPlayer.bass * 0.5 + 0.5;
        }

        if ( RadioUI.treble === target )
        {
          value = Radio.$.player._audioPlayer.treble  * 0.5 + 0.5;
        }

        let start = RadioUI.x0.asNumberFrom( rs );
        let end   = RadioUI.x1.asNumberFrom( rs );
        let y     = RadioUI.y.asNumberFrom( rs ); 

        let now = value * ( end - start ) + start;

        console.log( "syncing sliders", target, value, rs, now);
        SVGEditor.transform.to( rs, `translate(${now} ${y})` );      
      }
    );
  }

  private syncRadioFaders()
  {
    let radioFaders = RadioUI.radioFader.queryDocAll();

    console.log( "SYNC RF", radioFaders );

    radioFaders.forEach(
      ( rf )=>
      {
        let value = 0;
        let target = RadioUI.radioFader.from( rf );

        if ( RadioUI.bass === target )
        {
          value = Radio.$.player._audioPlayer.bass * 0.5 + 0.5;
        }

        if ( RadioUI.treble === target )
        {
          value = Radio.$.player._audioPlayer.treble  * 0.5 + 0.5;
        }

        let start = RadioUI.y0.asNumberFrom( rf );
        let end   = RadioUI.y1.asNumberFrom( rf );
        let x    = RadioUI.x.asNumberFrom( rf ); 

        let now = value * ( end - start ) + start;

        console.log( "syncing faders", target, value, rf, now);
        SVGEditor.transform.to( rf, `translate(${x} ${now})` );      
      }
    );
  }
  
  private _addFaderAreaChanger( fa:Element )
  {
    console.log( "AS", fa );
    let onMove = 
    ( e:TouchEvent )=>
    {
      let relative = DOMHitTest.getNormalizedPointerPosition( e, fa );
      let slideValue = 1 - MathX.clamp01( relative.y );
      let settingsValue = slideValue * 2 - 1;
      
      //let target = RadioUI.sliderArea.queryValueInDoc( sa );

      //this.setVolume( volumeValue );
      //Radio.$.player.set = volumeValue;

      let button = RadioUI.faderArea.queryValueInDoc( fa );

      let target = RadioUI.radioFader.from( button );
      
      if ( RadioUI.bass === target )
      {
        Radio.$.player._audioPlayer.setBass( settingsValue );
      }

      if ( RadioUI.treble === target )
      {
        Radio.$.player._audioPlayer.setTreble( settingsValue );
      }

      if ( button )
      {
        let start = RadioUI.y0.asNumberFrom( button );
        let end   = RadioUI.y1.asNumberFrom( button );
        let x     = RadioUI.x.asNumberFrom( button ); 

        let now = slideValue * ( end - start ) + start;

        SVGEditor.transform.to( button, `translate(${x} ${now})` );      
      }

      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();

    }
    
    OnDrag.add( fa as HTMLElement, onMove );

    //OnTouchMove.add( fa as HTMLElement, onMove );
  }

  private _addSliderAreaChanger( sa:Element )
  {
    console.log( "AS", sa );
    let onMove = 
    ( e:TouchEvent|MouseEvent )=>
    {
      let relative = DOMHitTest.getNormalizedPointerPosition( e, sa );
      let slideValue = MathX.clamp01( relative.x );
      let settingsValue = slideValue * 2 - 1;
      
      //let target = RadioUI.sliderArea.queryValueInDoc( sa );

      //this.setVolume( volumeValue );
      //Radio.$.player.set = volumeValue;

      let button = RadioUI.sliderArea.queryValueInDoc( sa );

      let target = RadioUI.radioSlider.from( button );
      
      if ( RadioUI.bass === target )
      {
        Radio.$.player._audioPlayer.setBass( settingsValue );
      }

      if ( RadioUI.treble === target )
      {
        Radio.$.player._audioPlayer.setTreble( settingsValue );
      }

      if ( button )
      {
        let start = RadioUI.x0.asNumberFrom( button );
        let end   = RadioUI.x1.asNumberFrom( button );
        let y     = RadioUI.y.asNumberFrom( button ); 

        let now = slideValue * ( end - start ) + start;

        SVGEditor.transform.to( button, `translate(${now} ${y})` );      
      }

      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();

    }

    OnDrag.add( sa as HTMLElement, onMove );
  }

  private _addTouchVolumeChanger( vs:Element )
  {
    let callback = 
    ( e:TouchEvent )=>
    {
      let relative = DOMHitTest.getNormalizedPointerPosition( e, vs );
      let volumeValue = MathX.clamp01( relative.x );
      this.setVolume( volumeValue );
      Radio.$.player.mainVolume = volumeValue;

      let button = RadioUI.volumeSlider.queryValueInDoc( vs );

      if ( button )
      {
        let start = RadioUI.start.asNumberFrom( button );
        let end   = RadioUI.end.asNumberFrom( button );

        let now = volumeValue * ( end - start ) + start;

        SVGEditor.transform.to( button, `translate(${now} 0)` );
      
      }

      e.stopPropagation();
      e.stopImmediatePropagation();
      e.preventDefault();

    }

    OnDrag.add( vs as HTMLElement, callback );

    //OnTouchEnd.add( vs as HTMLElement, callback );
    //OnTouchMove.add( vs as HTMLElement, callback );
       
    
  }


  private _cachedVolume = 0;
  private _cachedPageY  = 0;
  private _nextVolume   = 0;

  private _addVolumeChanger( element:Element )
  {
    let onMove = ( e:TouchEvent|MouseEvent ) => 
    { 
      console.log( e );
      if ( e.type === "mouseup" || e.type === "touchend"  )
      {
        RadioUI.dragging.removeFrom( element );
        Rokojori.$.freeCursor();

        return;
      }

      let position = DOMHitTest.getPointerPosition( e );

      if ( e.type === "mousedown" || e.type === "touchstart" )
      {
        this._cachedVolume = Radio.$.player.mainVolume;
        this._cachedPageY = position.y;

        RadioUI.dragging.set( element );
        Rokojori.$.lockCursor( "none" );
      }

      let range = RadioUI.volumeRange.asNumberFrom( element );
      let delta = ( position.y - this._cachedPageY ) / - range;
      this._nextVolume = MathX.clamp01( delta + this._cachedVolume );

      this.setVolume( this._nextVolume );
      Radio.$.player.mainVolume = this._nextVolume;  
      
    };

    OnDrag.add( element as HTMLElement, onMove );


    /*let removeListeners = ()=>
    {      
      OnMouseMove.remove( document.body, onMove );
      OnMouseUp.remove( document.body, removeListeners );
      RadioUI.dragging.removeFrom( element );
      Rokojori.$.freeCursor();
    }

    OnMouseDown.add( element as HTMLElement, 
      ( e ) =>
      {
        

        OnMouseMove.add( document.body, onMove );
        OnMouseUp.add( document.body, removeListeners );
        OnMouseLeave.add( document.body, removeListeners );

        
      }
    );
    */
    
  }

  setLeftInfo( info:SongInfo )
  {
    this._setText( RadioUI.artists, info.artists.toUpperCase() );
    this._setText( RadioUI.title, info.title.toUpperCase() );
    this._setText( RadioUI.duration, RadioUI.formatSeconds( info.duration ) );
    this._setText( RadioUI.time, "--:--" );
  }

  setInfos( playingSongs:PlayingSongInfo[] )
  {
    this._setText( RadioUI.artists, playingSongs[ 0 ].songInfo.artists.toUpperCase() );
    this._setText( RadioUI.title, playingSongs[ 0 ].songInfo.title.toUpperCase() );
    this._setText( RadioUI.nextArtists, playingSongs[ 1 ].songInfo.artists.toUpperCase() );
    this._setText( RadioUI.nextTitle, playingSongs[ 1 ].songInfo.title.toUpperCase() );
    this._setText( RadioUI.duration, RadioUI.formatSeconds( playingSongs[ 0 ].songInfo.duration ) );
    this._setText( RadioUI.time, "--:--" );
  }

  _lastOpacity = 0;
  _rotaters:Element[];

  updateRotaters()
  {
    let time = new Date().getTime();

    this._rotaters = this._rotaters || RadioUI.svgRotate.queryDocAll();

    this._rotaters.forEach(
      ( r ) =>
      {
        let durationMS = RadioUI.svgRotate.asNumberFrom( r ) * 1000;
        let loopState = ( time % durationMS ) / durationMS;
        let angle = loopState * 360;
        let value = SVGEditor.transform.from( r );

        let beforeValue = value;
        if ( value === null )
        {
          return;
        }

        value = value.replace( /rotate\(.+?,/, `rotate(${angle},` );

        console.log( angle, beforeValue, value );
        SVGEditor.transform.to( r );

      }
    )
  }

  _playHeads:Element[];

  setPlayState( seconds:number, state:number )
  {
    

    if ( isNaN( state ) )
    {
      state = 0;
    }


    let opacityBlend = 1;
    let blendPhase = 0.05;
    let endingBlendPhase = 1 - blendPhase;
    let scaler = 1 / blendPhase;

    //this._setText( RadioUI.time, RadioUI.formatSeconds( seconds ) );    

    this._playHeads = this._playHeads || RadioUI.playHead.queryDocAll();

    this._playHeads.forEach(
      ( playHead )=>
      {
        let y  = RadioUI.y.asNumberFrom( playHead );
        let x0 = RadioUI.x0.asNumberFrom( playHead ); 
        let x1 = RadioUI.x1.asNumberFrom( playHead ); 

        let x = MathX.lerp( x0, x1, state );
        let transformValue = `translate(${x},${y})`;

        SVGEditor.transform.to( playHead, transformValue );
        
        SVGEditor.style.removeFrom( playHead );
        SVGEditor.opacity.to( playHead, opacityBlend + "");
        
      }
    )


    if ( DOMOrientation.isPortrait )
    {
      return;
    }

    


    

    if ( state < blendPhase )
    {
      opacityBlend = state * scaler;
    }
    else if ( state > endingBlendPhase )
    {
      opacityBlend = 1 - ( state - endingBlendPhase ) * scaler;
    }

    if ( this._lastOpacity !== opacityBlend )
    {
      this._lastOpacity = opacityBlend;

      RadioUI.artists.queryDocAll().forEach(
        ( r:SVGElement )=>
        {
          r.style.opacity = opacityBlend + "";
        }
      );
  
      RadioUI.title.queryDocAll().forEach(
        ( r:SVGElement )=>
        {
          r.style.opacity = opacityBlend +"";
        }
      );
    }

    

    
   
  }
  
  setHiFi( bass:number, treble:number )
  {
    Radio.$.player._audioPlayer.setHiFiSettings( bass, treble );
    this.syncRadioSliders();
    this.syncRadioFaders();
  }

  setVolume( value:number )
  {
    let volumeKnobs = RadioUI.volumeKnob.queryDocAll();

    volumeKnobs.forEach(
      ( volumeKnob )=>
      {
        let origin = RadioUI.origin.from( volumeKnob );
        let a0 = RadioUI.a0.asNumberFrom( volumeKnob );
        let a1 = RadioUI.a1.asNumberFrom( volumeKnob );
        let angle = MathX.lerp( a0, a1, value );
        let style = `transform: rotate(${angle}deg)`;
        SVGEditor.transformOrigin.to( volumeKnob, origin );
        SVGEditor.style.to( volumeKnob, style );
      }
    )

    let volumeSliders = RadioUI.volumeSlider.queryDocAll();

    volumeSliders.forEach(
      ( vs ) =>
      {
        let button = RadioUI.volumeSlider.queryValueInDoc( vs );

      if ( button )
      {
        let start = RadioUI.start.asNumberFrom( button );
        let end   = RadioUI.end.asNumberFrom( button );

        let now = value * ( end - start ) + start;

        SVGEditor.transform.to( button, `translate(${now} 0)` );
      
      }
      }
    );


    let displayValue = Math.round( value * 125 ) + "";        
    if ( displayValue.length < 2 )
    {
      displayValue = "0" + displayValue;
    }

    if ( displayValue.length < 2 )
    {
      displayValue = "0" + displayValue;
    }

    let volumeInfos = RadioUI.volumeInfo.queryDocAll();

    volumeInfos.forEach(
      ( volumeInfo )=>
      {
        Inkscape.setText( volumeInfo as SVGElement, displayValue );
      }
    )
  }

  _cache = new Map<ElementAttribute,Element[]>();

  private _setText( elementAttribute:ElementAttribute, text:string )
  {
    if ( ! this._cache.has( elementAttribute ) )
    {
      let cachedElements = elementAttribute.queryDocAll();
      //cachedElements = cachedElements.filter( c => HTMLEditor.checkFastIfVisible( c as HTMLElement ) );

      this._cache.set( elementAttribute, cachedElements );
    }

    let elements = this._cache.get( elementAttribute );

    elements.forEach(
      ( e )=>
      {
        Inkscape.setText( e as SVGElement, text );
      }
    )
  }
}